import { call, select, takeLatest } from 'redux-saga/effects';
import * as api from '@api/qcTask';
import { fetchDICOMResource } from '@api/DICOMResource';

import { actions, selectQCTaskState } from './QCTask.slice';
import {
  QCTaskAssignAction,
  QCTaskChangeStatusAction,
  QCTaskFetchAction,
} from './QCTask.types';
import { Resp, dispatch } from '../helpers';
import { QcStatusEnum } from '@dynamika-shared/types/status';
import { GO_TO_ROUTE_ACTION, runToast } from '../tools';
import { routesPath } from '@router/routes';

export const messages = {
  assignSuccess: 'Task assigned successfully',
  assignFailure: 'Failed to assign task',
  changeTaskStatusSuccess: 'Task status updated',
  gettingTaskDataFailure: 'Getting task data',
};

export function* fetchQCTaskWorker(action: QCTaskFetchAction) {
  try {
    const { data: task }: Resp<typeof api.fetchQCTask> = yield call(
      api.fetchQCTask,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );
    const { url: DICOMResource }: Resp<typeof fetchDICOMResource> = yield call(
      fetchDICOMResource,
      action.payload.clinicalTrialId
    );
    yield dispatch(actions.fetchQCTaskSuccess({ task, DICOMResource }));
  } catch (error) {
    yield call(runToast, 'error', messages.gettingTaskDataFailure);
    yield dispatch(actions.fetchQCTaskFailure('fetch error'));

    yield dispatch({
      type: GO_TO_ROUTE_ACTION,
      payload: routesPath.qc,
    });
  }
}

export function* QCTaskAssignWorker(action: QCTaskAssignAction) {
  try {
    const { clinicalTrialId, taskId } = action.payload;
    yield call(api.qcTaskAssignToMe, clinicalTrialId, taskId);

    const { data: taskData }: ReturnType<typeof selectQCTaskState> =
      yield select(selectQCTaskState);

    if (taskData?.status === QcStatusEnum.PENDING) {
      yield dispatch(
        actions.changeStatus({
          clinicalTrialId,
          taskId,
          status: QcStatusEnum.IN_PROGRESS,
        })
      );
    }

    yield call(fetchQCTaskWorker, {
      payload: { taskId, clinicalTrialId },
      type: '',
    });

    yield call(runToast, 'success', messages.assignSuccess);

    yield dispatch(actions.assignSuccess(false));
  } catch (error) {
    yield call(runToast, 'error', messages.assignFailure);
    yield dispatch(actions.assignFailure(error as Error));
  }
}

export function* QCTaskChangeStatusWorker(action: QCTaskChangeStatusAction) {
  try {
    yield call(
      api.qcTaskStatus,
      action.payload.clinicalTrialId,
      action.payload.taskId,
      action.payload.status
    );
    yield call(runToast, 'success', messages.changeTaskStatusSuccess);
    yield dispatch(actions.changeStatusSuccess(action.payload.status));
  } catch (error) {
    yield dispatch(actions.changeStatusFailure(error as Error));
  }
}

export function* QCTaskSaga() {
  yield takeLatest(actions.fetchQCTask.type, fetchQCTaskWorker);
  yield takeLatest(actions.assign.type, QCTaskAssignWorker);
  yield takeLatest(actions.changeStatus.type, QCTaskChangeStatusWorker);
}
